import React from 'react';
import AuthChooser from '../auth/AuthChooser';

const normalizeActionLabel = (value) => String(value || '').trim();

const adaptCompletionActionLabel = ({
  label,
  role = 'tasker',
  fallback,
  isSecondary = false,
}) => {
  const raw = normalizeActionLabel(label);
  if (!raw) return fallback;

  if (isSecondary) {
    if (
      /^use jgk locally$/i.test(raw) ||
      /^use jgk locally again$/i.test(raw) ||
      /^find nearby offers$/i.test(raw) ||
      /^open nearby offers$/i.test(raw) ||
      /^spend jgk on local offers$/i.test(raw) ||
      /^review nearby trusted places that accept jgk now$/i.test(raw)
    ) {
      return 'Use JGK locally';
    }
    return raw;
  }

  if (/^resume accepted task$/i.test(raw)) return 'Find next local action';
  if (/^continue to active task$/i.test(raw)) return 'Find next local action';
  if (/^resume reserved offering$/i.test(raw)) return 'Use JGK locally';
  if (/^continue to reserved offering$/i.test(raw)) return 'Use JGK locally';
  if (/^find another task$/i.test(raw)) return 'Find next local action';
  if (/^find nearby offers$/i.test(raw)) return 'Use JGK locally';
  if (/^use jgk locally again$/i.test(raw)) return 'Use JGK locally';
  if (/^continue earning with nearby high-value localops opportunities$/i.test(raw)) {
    return 'Earn again';
  }
  if (/^create another task$/i.test(raw) || /^create next task$/i.test(raw)) {
    return 'Post another task';
  }
  if (/^post another offering$/i.test(raw)) return 'Create another offer';
  if (/^continue$/i.test(raw)) return '';

  return raw;
};

// PostCompletionScreen renders completion truth from PlayerMap/backend contract.
// It is UI-only: no side effects, no routing, no fetching.
export default function PostCompletionScreen({
  open = true,
  context,
  completionContext,
  onClose,
  onCashOut,
  onUseLocally,
  onShare,
  onDoAnother,
  onCreateNext,
  onPrimaryAction,
  onSecondaryAction,
  authProps = null,
  playerBalance = null,
}) {
  const ctx = completionContext || context;
  if (!open || !ctx) return null;

  const {
    kind,
    entityType,
    role = 'tasker',
    amountUSD,
    amountJGK,
    title,
    counterpartyName,
  } = ctx;

  const header = (() => {
    if (title) return title;
    if (kind === 'offering_fulfilled') return 'Offering fulfilled';
    if (entityType === 'offering') return 'Offering completed';
    return 'Task completed';
  })();

  const roleLabel = (() => {
    if (role === 'creator') return 'Creator';
    if (role === 'buyer') return 'Buyer';
    if (role === 'provider') return 'Provider';
    return 'Tasker';
  })();

  const localValue = ctx?.localValueCreated || {};
  const resolvedAmountUSD = Number.isFinite(Number(amountUSD))
    ? Number(amountUSD)
    : Number.isFinite(Number(localValue.amountUSD))
    ? Number(localValue.amountUSD)
    : null;
  const resolvedAmountJGK = Number.isFinite(Number(amountJGK))
    ? Math.round(Number(amountJGK))
    : Number.isFinite(Number(localValue.amountJGK))
    ? Math.round(Number(localValue.amountJGK))
    : Number.isFinite(Number(resolvedAmountUSD))
    ? Math.round(Number(resolvedAmountUSD) * 1000)
    : null;

  const proofLineUSD =
    Number.isFinite(Number(resolvedAmountUSD)) && resolvedAmountUSD !== null
      ? `$${Number(resolvedAmountUSD).toFixed(2)}`
      : null;
  const proofLineJGK =
    Number.isFinite(Number(resolvedAmountJGK)) && resolvedAmountJGK !== null
      ? `${Number(resolvedAmountJGK)} JGK`
      : null;

  const entityLabel = entityType === 'offering' ? 'Offering' : 'Task';
  const outcomeSubline = counterpartyName
    ? `Verified with ${counterpartyName}`
    : `Verified ${entityLabel.toLowerCase()} completion`;
  const outcomeMetaLine = `${roleLabel} | ${entityLabel}`;

  const actionCredit = ctx?.actionCredit || {};
  const actionCreditAwarded = actionCredit?.awarded === true;
  const actionCreditDuplicate = actionCredit?.duplicate === true;
  const actionCreditEligible =
    actionCredit?.eligibleForViewer === true
      ? true
      : actionCredit?.eligibleForViewer === false
      ? false
      : null;

  const actionCreditTotal = Number.isFinite(Number(actionCredit?.total))
    ? Number(actionCredit.total)
    : Number.isFinite(Number(ctx?.trust?.total))
    ? Number(ctx.trust.total)
    : null;

  const trustLabel =
    actionCredit?.trustLabel ||
    ctx?.trust?.label ||
    (Number(actionCreditTotal || 0) >= 1 ? 'Verified Local' : null);
  const trustUnlocked =
    actionCredit?.trustUnlocked === true || ctx?.trust?.unlocked === true;
  const isFirstUnlockMilestone =
    actionCreditAwarded &&
    trustUnlocked &&
    (!Number.isFinite(Number(actionCreditTotal)) || Number(actionCreditTotal) <= 1);
  const outcomeKicker = trustUnlocked ? 'Verified milestone' : 'Verified completion';

  const hasRewardAmount =
    Number.isFinite(Number(resolvedAmountUSD)) &&
    Number.isFinite(Number(resolvedAmountJGK));
  const zeroRewardState =
    hasRewardAmount &&
    Number(resolvedAmountUSD) <= 0 &&
    Number(resolvedAmountJGK) <= 0;

  const actionCreditHeadline = (() => {
    if (actionCreditAwarded) return '+1 Action Credit';
    if (actionCreditDuplicate) return 'Action Credit unchanged';
    if (actionCreditEligible === false) {
      return 'Verified by you';
    }
    if (actionCreditEligible === true) return 'Action Credit syncing';
    return 'Action Credit unavailable';
  })();

  const actionCreditDetailLine = (() => {
    if (actionCreditAwarded && isFirstUnlockMilestone) {
      return 'First credited completion reached Verified Local.';
    }
    if (actionCreditAwarded) {
      return 'Credit applied to your verified record.';
    }
    if (actionCreditDuplicate) {
      return 'Completion verified; no additional credit added.';
    }
    if (actionCreditEligible === false) {
      return 'You confirmed this completion. Credit was applied to the person who completed the work.';
    }
    if (actionCreditEligible === true) {
      return 'Completion verified; credit is syncing now.';
    }
    return 'Completion verified; credit status unavailable.';
  })();

  const trustStatusLine = (() => {
    if (trustLabel && isFirstUnlockMilestone) {
      return `Trust: ${trustLabel} unlocked`;
    }
    if (trustLabel) return `Trust: ${trustLabel}`;
    return 'Trust: Not unlocked yet';
  })();

  const verifiedRecordLine = (() => {
    if (actionCreditAwarded) {
      return 'Verified record updated.';
    }
    if (actionCreditDuplicate) {
      return 'Verified record confirmed.';
    }
    if (actionCreditEligible === false) {
      return 'Verified record updated with your confirmation.';
    }
    return 'Verified record updated.';
  })();

  const rewardHeadline = (() => {
    if (zeroRewardState) return 'Verified contribution';
    return proofLineUSD || 'Reward pending';
  })();

  const rewardSubline = (() => {
    if (zeroRewardState) return 'No payout attached to this completion';
    return proofLineJGK || 'JGK amount unavailable';
  })();

  const contributionLine = (() => {
    if (zeroRewardState) {
      return 'Verified contributions count even when no payout is attached.';
    }
    return 'Verified completion strengthens both payout history and trust.';
  })();

  const walletLine =
    Number.isFinite(Number(playerBalance)) && Number(playerBalance) > 0
      ? `Balance: ${Number(playerBalance).toLocaleString('en-US')} JGK ($${(
          Number(playerBalance) / 1000
        ).toFixed(2)})`
      : null;

  const shareHandler = () => {
    const payload =
      ctx?.sharePayload ||
      {
        entityId: ctx?.entityId || ctx?.taskId || ctx?.id,
        entityType: ctx?.entityType || 'task',
        amountUSD: resolvedAmountUSD ?? null,
        amountJGK: resolvedAmountJGK ?? null,
      };
    if (onShare) onShare(payload);
  };

  const renderButton = (label, handler, variant = 'primary') => {
    if (!handler || !label) return null;
    const styles = {
      primary: {
        background: 'linear-gradient(90deg, #22d3ee, #06b6d4)',
        color: '#0b1220',
      },
      ghost: {
        background: 'rgba(148,163,184,0.12)',
        color: '#e5e7eb',
        border: '1px solid rgba(148,163,184,0.18)',
      },
    }[variant];
    return (
      <button
        type="button"
        onClick={handler}
        style={{
          width: '100%',
          padding: '12px 14px',
          borderRadius: 12,
          border: 'none',
          fontWeight: 700,
          cursor: 'pointer',
          ...styles,
        }}
      >
        {label}
      </button>
    );
  };

  const renderActions = () => {
    if (ctx?.authRequired && authProps) {
      return (
        <AuthChooser
          headerText={authProps.headerText || 'Sign in to continue'}
          subText={authProps.subText}
          onPrimary={authProps.onPrimary}
          onPrimaryStart={authProps.onPrimaryStart}
          primaryDisabled={authProps.primaryDisabled}
          email={authProps.email}
          onEmailChange={authProps.onEmailChange}
          emailError={authProps.emailError}
          onEmailErrorClear={authProps.onEmailErrorClear}
          onSendMagicLink={authProps.onSendMagicLink}
          magicStatus={authProps.magicStatus}
        />
      );
    }

    const primaryLabel = adaptCompletionActionLabel({
      label: ctx?.nextAction?.label,
      role,
      fallback: 'Find next local action',
    });
    const primaryHandler = onPrimaryAction || onDoAnother || onCreateNext || null;
    const secondaryLabel = adaptCompletionActionLabel({
      label: ctx?.secondaryAction?.label,
      role,
      fallback: 'Use JGK locally',
      isSecondary: true,
    });
    const secondaryHandler = onSecondaryAction || onUseLocally || onCashOut || null;
    const showPrimary = !!(primaryHandler && primaryLabel);
    const showSecondary = !!(secondaryHandler && secondaryLabel);

    return (
      <>
        {showPrimary ? renderButton(primaryLabel, primaryHandler, 'primary') : null}
        {showSecondary
          ? renderButton(secondaryLabel, secondaryHandler, showPrimary ? 'ghost' : 'primary')
          : null}
        {onShare ? renderButton('Share', shareHandler, 'ghost') : null}
        {renderButton('Close', onClose, 'ghost')}
      </>
    );
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(8,12,20,0.82)',
        backdropFilter: 'blur(10px)',
        zIndex: 2000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 20,
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 540,
          background: '#0f172a',
          border: '1px solid rgba(148,163,184,0.18)',
          boxShadow: '0 22px 50px rgba(0,0,0,0.45)',
          borderRadius: 20,
          padding: 28,
          color: '#e5e7eb',
          textAlign: 'left',
        }}
      >
        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: 1.2,
            textTransform: 'uppercase',
            color: '#93c5fd',
            marginBottom: 8,
          }}
        >
          {outcomeKicker}
        </div>
        <div style={{ fontSize: 22, fontWeight: 800, marginBottom: 8, lineHeight: 1.25 }}>
          {header}
        </div>
        <div style={{ fontSize: 14, color: '#9fb6d1', marginBottom: 8 }}>
          {outcomeSubline}
        </div>
        <div style={{ fontSize: 13, color: '#94a3b8', marginBottom: 18 }}>
          {outcomeMetaLine}
        </div>

        <div
          style={{
            marginBottom: 20,
            padding: 18,
            borderRadius: 14,
            background: 'rgba(34,211,238,0.08)',
            border: '1px solid rgba(34,211,238,0.25)',
            textAlign: 'left',
          }}
        >
          <div style={{ fontSize: 15, color: '#a5b4fc', marginBottom: 6 }}>
            Reward
          </div>
          <div
            style={{
              fontSize: zeroRewardState ? 24 : 34,
              fontWeight: 800,
              marginBottom: 6,
              lineHeight: 1.1,
              color: zeroRewardState ? '#cbd5e1' : '#f8fafc',
            }}
          >
            {rewardHeadline}
          </div>
          <div style={{ fontSize: 15, color: '#94a3b8', marginBottom: 10 }}>
            {rewardSubline}
          </div>
          {zeroRewardState ? (
            <div style={{ fontSize: 13, color: '#94a3b8' }}>
              {`Recorded payout: ${proofLineUSD || '$0.00'} (${proofLineJGK || '0 JGK'})`}
            </div>
          ) : null}
        </div>

        <div
          style={{
            marginBottom: 16,
            padding: 16,
            borderRadius: 14,
            border: '1px solid rgba(96,165,250,0.25)',
            background: 'rgba(37,99,235,0.12)',
          }}
        >
          <div style={{ fontSize: 15, color: '#bfdbfe', marginBottom: 8 }}>
            Trust and record
          </div>
          <div style={{ fontSize: 15, color: '#dbeafe', marginBottom: 4 }}>
            {actionCreditHeadline}
          </div>
          <div style={{ fontSize: 13, color: '#cbd5e1', marginBottom: 8 }}>
            {Number.isFinite(Number(actionCreditTotal))
              ? `Action Credit: ${actionCreditTotal}`
              : 'Action Credit: unavailable'}{' '}
            | {trustStatusLine}
          </div>
          {isFirstUnlockMilestone ? (
            <div
              style={{
                marginBottom: 8,
                padding: '8px 10px',
                borderRadius: 10,
                border: '1px solid rgba(251,191,36,0.45)',
                background: 'rgba(245,158,11,0.16)',
                color: '#fde68a',
                fontSize: 13,
                fontWeight: 700,
              }}
            >
              Milestone: Verified Local unlocked with your first credited completion.
            </div>
          ) : null}
          <div style={{ fontSize: 13, color: '#cbd5e1', marginBottom: 6 }}>
            {actionCreditDetailLine}
          </div>
          <div style={{ fontSize: 13, color: '#cbd5e1' }}>{verifiedRecordLine}</div>
        </div>

        <div
          style={{
            fontSize: 13,
            fontWeight: 400,
            color: '#9fb6d1',
            marginBottom: 10,
          }}
        >
          {contributionLine}
        </div>
        {walletLine ? (
          <div
            style={{
              fontSize: 13,
              fontWeight: 400,
              color: '#9fb6d1',
              marginBottom: 16,
            }}
          >
            {walletLine}
          </div>
        ) : null}

        <div style={{ fontSize: 14, color: '#cbd5e1', marginBottom: 8 }}>Next step</div>
        <div style={{ display: 'grid', gap: 10 }}>{renderActions()}</div>
      </div>
    </div>
  );
}
